/**
 * PortalLoginPage.tsx
 *
 * Patient portal login — email + password + practice login name (account slug).
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { usePortalAuthStore } from '@/store/portalAuthStore';
import type { ApiResponse } from '@/types/api.types';
import type { PortalBranding, PortalPatientInfo } from '@/store/portalAuthStore';

interface PortalLoginResponse {
  token: string;
  patient: {
    patientID: number;
    accountID: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  branding: {
    logoUrl: string | null;
    primaryColor: string | null;
    practiceName: string | null;
  };
}

export function PortalLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const storeLogin = usePortalAuthStore((s) => s.login);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/portal';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginName, setLoginName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await apiClient.post<ApiResponse<PortalLoginResponse>>(
        '/auth/portal/login',
        { email: email.trim(), password, loginName: loginName.trim() },
      );
      const { token, patient, branding } = res.data.data;

      const patientInfo: PortalPatientInfo = {
        patientId: patient.patientID,
        accountId: patient.accountID,
        firstName: patient.firstName,
        lastName: patient.lastName,
        email: patient.email,
      };

      const brandingInfo: PortalBranding = {
        logo: branding.logoUrl,
        primaryColor: branding.primaryColor,
        practiceName: branding.practiceName,
      };

      storeLogin(token, patientInfo, brandingInfo);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(
        (err as { userMessage?: string })?.userMessage ?? 'Invalid credentials. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-slate-900">Patient Portal</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to view your health information</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 space-y-5">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="loginName" className="block text-sm font-medium text-slate-700 mb-1">
                Practice Name
              </label>
              <input
                id="loginName"
                type="text"
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your practice login name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email || !password || !loginName}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
